import Typography, { TypographyProps } from '@components/v2/ui/Typography/Typography';
import { Box, Grid, Skeleton } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';
import * as React from 'react';
import { SimpleSocialMedia } from '../../lib/SocialMedias';
import { Segment } from '../../modules/segments/segment.type';
import { SegmentsTranslation, translateSegments } from '../../modules/segments/useSegments';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { AddressDetails } from '../../types/address.types';
import { ShowroomTestIds } from '../../utils/testIds/Showroom.testIds';
import { Address } from '../CrudTabs/List/AddressListCustomer';
import useWidth from '../Hooks/useWidth';
import Section, { SectionProps } from './Section';
import Signature from './Signature';

export type AboutInfo = {
  name?: string;
  slogan?: string;
  site?: string;
  about?: string;
  logoUrl?: string;
  medias: SimpleSocialMedia[];
  mediumPrices?: string;
  segments: Segment[];
};

type AboutSectionProps = {
  sectionProps?: Partial<SectionProps>;
  aboutInfo?: Partial<AboutInfo>;
  loading: boolean;
  children?: React.ReactNode;
  address?: Address & {
    address: AddressDetails;
  };
  cpfcnpj?: string;
  companyName?: string;
  isPJ?: boolean;
};

const getGridAlign = (breakpoint: Breakpoint, defaultAlign: TypographyProps['align']) => {
  switch (breakpoint) {
    case 'xs':
      return 'center';
    case 'sm':
      return 'left';
    default:
      return defaultAlign;
  }
};

const AboutSection = ({
  sectionProps,
  aboutInfo,
  children,
  loading,
  address,
  cpfcnpj,
  companyName,
  isPJ,
}: AboutSectionProps) => {
  const { activeBreakpoint } = useWidth();
  const { isPortuguese } = useTranslation();

  return (
    <Section {...sectionProps}>
      <Grid
        container
        spacing={8}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Grid
          test-id={ShowroomTestIds.BRAND_INFO}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          {loading ? (
            <Skeleton width="100%" height={120} />
          ) : (
            <Signature
              name={aboutInfo?.name}
              slogan={aboutInfo?.slogan}
              site={aboutInfo?.site}
              avatar={aboutInfo?.logoUrl}
              medias={aboutInfo?.medias}
              address={address}
              cpfcnpj={cpfcnpj}
              companyName={companyName}
              isPJ={isPJ}
            />
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Box
            sx={{
              pb: 1,
            }}
          >
            <Typography
              align="right"
              variant="body1"
              color="text.primary"
              testId={ShowroomTestIds.TAG_DETAILS}
            >
              {aboutInfo?.segments
                ?.map((segment) =>
                  translateSegments(segment.name as keyof SegmentsTranslation, isPortuguese),
                )
                .join(' . ')}
            </Typography>
          </Box>
          <Typography
            variant="h5"
            color="text.primary"
            align={getGridAlign(activeBreakpoint, 'right')}
            testId={ShowroomTestIds.PRICE_TAG_DETAILS}
          >
            {loading ? <Skeleton variant="text" /> : aboutInfo?.mediumPrices}
          </Typography>
        </Grid>
        {children && <Grid size={12}>{children}</Grid>}
      </Grid>
    </Section>
  );
};

export default React.memo(AboutSection);
